/**
 * VideoPreloader
 *
 * Renderiza um `<video>` element invisível com `preload="auto"` pra
 * forçar o browser a baixar metadata + chunks iniciais do PRÓXIMO vídeo
 * da playlist enquanto o atual ainda está tocando.
 *
 * <p><b>Problema que resolve:</b> Em transições de mídia (current →
 * next), o `<video>` principal exibe "Carregando vídeo..." por ~1s
 * enquanto o browser:</p>
 * <ol>
 *   <li>Faz HTTP request da URL (ou lê o blob do IndexedDB)</li>
 *   <li>Parseia headers MP4/WebM</li>
 *   <li>Decoda o keyframe inicial</li>
 *   <li>Dispara {@code onCanPlay}</li>
 * </ol>
 * <p>Isso é inaceitável pra digital signage — o video seguinte deve
 * estar pronto pra rodar no momento que o anterior termina.</p>
 *
 * <p><b>Como funciona:</b></p>
 * <ul>
 *   <li>Componente fica montado no DOM com {@code display: none} e
 *       {@code muted}</li>
 *   <li>Quando {@code url} muda, dispara {@code video.load()} que
 *       baixa metadata + buffer inicial em background</li>
 *   <li>Browser cacheia o HTTP/blob response. Quando o `<video>`
 *       principal recebe a mesma URL, decoder reusa cache → tempo
 *       de transição cai de 1000ms pra <100ms</li>
 *   <li>Não tenta tocar — só pré-decoda. Sem áudio, sem progresso</li>
 * </ul>
 *
 * <p><b>Caveat:</b> Aloca buffer adicional na RAM nativa (browser
 * mantém o decoder context vivo até GC). Em TVs com WebView Tizen
 * 9.0, isso some pra ~10-30 MB por preloader — aceitável.</p>
 */
import { useEffect, useRef } from 'react';

interface VideoPreloaderProps {
  /**
   * URL do próximo vídeo pra pré-carregar. Pode ser blob:, file:, http:
   * Setar pra {@code null} desliga o preloader (libera buffer).
   */
  url: string | null;
}

const VideoPreloader = ({ url }: VideoPreloaderProps): JSX.Element | null => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!url) {
      // Limpa o src pra liberar buffer alocado pelo browser
      video.removeAttribute('src');
      video.load();
      return;
    }

    // Setar src + load() faz o browser disparar a fase de:
    // - Network (HTTP) ou blob read (IndexedDB)
    // - Demux do container (MP4/WebM)
    // - Decode do keyframe inicial
    // Sem chamar play() — mantém o video em estado HAVE_METADATA / HAVE_CURRENT_DATA
    if (video.src !== url) {
      video.src = url;
      video.load();
    }
  }, [url]);

  // Renderiza fora do fluxo visual, sem áudio, com preload máximo
  return (
    <video
      ref={videoRef}
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
        // top/left fora da tela pra evitar layout bug em browsers velhos
        top: -9999,
        left: -9999,
      }}
      muted
      playsInline
      preload="auto"
      // crossOrigin precisa bater com o video principal pra cache HTTP
      // ser compartilhado (browsers tratam requests CORS vs no-CORS como
      // entradas separadas no cache)
      crossOrigin="anonymous"
      aria-hidden="true"
      tabIndex={-1}
    />
  );
};

export default VideoPreloader;
